import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import AuthLayout from "../components/layout/AuthLayout";
import PasswordInput from "../components/PasswordInput";
import { OTPInput, Input, Button } from "../components/ui";

const ForgotPassword = () => {
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const navigate = useNavigate();
  const { forgotPassword, resetPassword, resendPasswordOTP } = useAuth();

  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => (prev <= 1 ? 0 : prev - 1));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleResendOTP = async () => {
    try {
      await resendPasswordOTP(formik.values.email);
      setResendTimer(60);
      toast.success("OTP resent to your email");
    } catch (error) {
      toast.error(error.message || "Failed to resend OTP");
    }
  };

  const getForgotPasswordSchema = (isOtpSent) =>
    Yup.object().shape({
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      otp: isOtpSent ? Yup.string().required("OTP is required") : Yup.string(),
      newPassword: isOtpSent
        ? Yup.string()
            .min(8, "Password must be at least 8 characters")
            .required("New password is required")
        : Yup.string(),
      confirmPassword: isOtpSent
        ? Yup.string()
            .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
            .required("Confirm password is required")
        : Yup.string(),
    });

  const formik = useFormik({
    initialValues: {
      email: "",
      otp: "",
      newPassword: "",
      confirmPassword: "",
      isOtpSent: false,
    },
    validationSchema: getForgotPasswordSchema(isOtpSent),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!isOtpSent) {
          await forgotPassword(values.email);
          setIsOtpSent(true);
          setResendTimer(60);
          toast.success("OTP sent to your email");
        } else {
          await resetPassword(values.email, values.otp, values.newPassword);
          toast.success("Password reset successfully");
          navigate("/login");
        }
      } catch (error) {
        formik.setFieldError(
          "formError",
          error?.response?.data?.message ||
            (isOtpSent ? "Failed to reset password" : "Failed to send OTP"),
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <AuthLayout>
      <div className="space-y-8">
        <div>
          <h2 className="font-heading text-4xl font-bold text-gray-900">
            Reset your password
          </h2>
          <p className="text-sm text-gray-500 mt-3">
            Enter your email and we'll send you a verification code.
          </p>
        </div>

        <form
          onSubmit={(e) => {
            formik.handleSubmit(e);
            e.preventDefault();
          }}
          className="space-y-5"
        >
          <Input
            id="email"
            name="email"
            type="email"
            label="Email"
            required
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="mail@example.com"
            disabled={isOtpSent}
            error={
              formik.errors.email && formik.touched.email
                ? formik.errors.email
                : undefined
            }
          />

          {isOtpSent && (
            <>
              <div className="animate-fadeIn">
                <OTPInput
                  name="otp"
                  value={formik.values.otp}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.errors.otp && formik.touched.otp
                      ? formik.errors.otp
                      : undefined
                  }
                  resendTimer={resendTimer}
                  onResend={handleResendOTP}
                />
              </div>
              <div>
                <label
                  htmlFor="newPassword"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  New password <span className="text-red-500">*</span>
                </label>
                <PasswordInput
                  id="newPassword"
                  name="newPassword"
                  autoComplete="new-password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.newPassword}
                  placeholder="Min. 8 characters"
                  error={
                    formik.errors.newPassword && formik.touched.newPassword
                      ? formik.errors.newPassword
                      : undefined
                  }
                />
              </div>
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Confirm password <span className="text-red-500">*</span>
                </label>
                <PasswordInput
                  id="confirmPassword"
                  name="confirmPassword"
                  autoComplete="new-password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.confirmPassword}
                  placeholder="Confirm your password"
                  error={
                    formik.errors.confirmPassword &&
                    formik.touched.confirmPassword
                      ? formik.errors.confirmPassword
                      : undefined
                  }
                />
              </div>
            </>
          )}

          {formik.errors.formError && (
            <div className="text-red-500 text-xs bg-red-50 border border-red-200 p-3 rounded-xl flex items-center gap-2">
              <InformationCircleIcon className="h-4 w-4 shrink-0" />
              <span>{formik.errors.formError}</span>
            </div>
          )}

          <Button
            type="submit"
            fullWidth
            loading={formik.isSubmitting}
            disabled={formik.isSubmitting}
            className="py-3"
          >
            {isOtpSent ? "Reset password" : "Send OTP"}
          </Button>

          <p className="text-center text-sm text-gray-500">
            <Link
              to="/login"
              className="font-medium text-primary hover:text-primary-dark"
            >
              Back to login
            </Link>
          </p>
        </form>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
